<template>
  <!--
    ANNOUNCEMENTS modal (new channel layout).

    Per the Figma "Home / Announcements" overlay (node 18681:28874): a large
    centered surface with a header row ("Announcements" title + circular X close
    icon-button) and a scrollable body that lists the channel threads. The thread
    list, its queries, pagination and empty state all live in <ThreadsSection> —
    this component only HOSTS that list inside the V2 modal chrome. It carries no
    thread data of its own.

    Built on the V2 modal system: ModalRoot (#body slot) drives teleport to
    #portal-root, the backdrop, focus-trap and Escape-to-close; useJustOneOpenModal
    coordinates the single-open-modal queue. The surface and close-button chrome
    are rendered here with V2 theme-adaptive tokens (so they read correctly on the
    light theme, matching the Figma) instead of the deprecated ModalBody/ModalBodyV2
    chrome, which hardcodes V1 dark-overlay tokens that would clash with the light
    surface.
  -->
  <ModalRoot
    v-model="isOpen"
    class="data-[modal-wrapper]:*:!w-full"
    @close="close"
  >
    <template #body="{ close: closeModal }">
      <section
        data-testid="channel-announcements-modal"
        class="mx-6 flex max-h-[calc(100dvh-4rem)] backdrop-blur-2xl w-full max-w-6xl flex-col overflow-hidden rounded-3xl bg-surface shadow-xl sm:mx-auto"
      >
        <!-- Header: title left, circular close icon-button right (Figma). Pinned
             so the threads list scrolls beneath it. -->
        <header
          class="flex shrink-0 flex-row items-center justify-between gap-4 px-6 pb-4 pt-6 sm:px-8 sm:pt-8"
        >
          <h2 class="text-2xl font-semibold text-text">
            {{ t("channel.announcementsModal.title") }}
          </h2>

          <button
            type="button"
            data-testid="channel-announcements-modal-close"
            class="flex size-10 shrink-0 items-center justify-center rounded-full bg-surface text-icon-brand shadow-md transition-colors hover:bg-surface-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-border-brand"
            :aria-label="t('channel.announcementsModal.close')"
            data-dismiss="modal"
            @click="closeModal"
          >
            <Icon icon="mdi:close" class="size-6" aria-hidden="true" />
          </button>
        </header>

        <!-- Scrollable threads list (the announcements). -->
        <div class="min-h-0 flex-1 overflow-y-auto px-6 pb-6 sm:px-8 sm:pb-8">
          <ThreadsSection empty-variant="subtle" hide-header />
        </div>
      </section>
    </template>
  </ModalRoot>
</template>

<script setup lang="ts">
import { Icon } from "@iconify/vue";
import { useI18n } from "vue-i18n";
import ModalRoot from "@/components/ui/modal/modal-root.vue";
import ThreadsSection from "@/components/channel/threads/threads-section.vue";
import { useJustOneOpenModal } from "@/composables/buses/use-modal-bus";

const isOpen = defineModel<boolean>("isOpen", { required: true });

const { t } = useI18n();

useJustOneOpenModal(isOpen);

function close() {
  isOpen.value = false;
}
</script>
